// /src/StatusContext.js

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { statusSocket } from "../utils/Socket";

const StatusContext = createContext();
export const useStatus = () => useContext(StatusContext);

function ts() {
  return new Date().toISOString();
}

export function StatusProvider({ children }) {
  const [networkStatus, setNetworkStatus] = useState(
    navigator.onLine ? "online" : "offline"
  );
  const [serverStatus, setServerStatus] = useState("ok");
  const [maintenance, setMaintenance] = useState(false);
  const [lockMessage, setLockMessage] = useState(null);
  const [registration, setRegistration] = useState(false); // English comment: NEW
  const httpBootstrapOkRef = useRef(false);

  // ---------------------------------------------------------------------
  // Initial HTTP bootstrap
  // ---------------------------------------------------------------------
  useEffect(() => {
    async function bootstrapStatus() {
      try {
        const res = await axios.get("/api/get-status", { withCredentials: true });

        if (typeof res.data.maintenance === "boolean") {
          setMaintenance(res.data.maintenance);
        }
        if (typeof res.data.registration === "boolean") {
          setRegistration(res.data.registration);
        }
        if (typeof res.data.lockMessage === "string" || res.data.lockMessage === null) {
          setLockMessage(res.data.lockMessage);
        }
        if (res.data.serverStatus) {
          setServerStatus(res.data.serverStatus);
        } else {
          setServerStatus("ok");
        }
        httpBootstrapOkRef.current = true;
      } catch (e) {
        console.error(`[${ts()}][StatusContext] Failed to bootstrap status`, e);
        httpBootstrapOkRef.current = false;
        setServerStatus("unreachable");
      }
    }

    bootstrapStatus();
  }, []);

  // ---------------------------------------------------------------------
  // Browser network changes
  // ---------------------------------------------------------------------
  useEffect(() => {
    const goOnline = () => {
      console.log(`[${ts()}][StatusContext] navigator ONLINE`);
      setNetworkStatus("online");
    };

    const goOffline = () => {
      console.log(`[${ts()}][StatusContext] navigator OFFLINE`);
      setNetworkStatus("offline");
      setServerStatus("unreachable");
    };

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // ---------------------------------------------------------------------
  // WebSocket listeners
  // ---------------------------------------------------------------------
  useEffect(() => {
    statusSocket.on("connect", () => {
      console.log(`[${ts()}][StatusContext][WS] CONNECTED (${statusSocket.id})`);
      setServerStatus("ok");
    });

    const probeServerStatus = async () => {
      try {
        const res = await axios.get("/api/get-status", { withCredentials: true });
        if (typeof res.data.maintenance === "boolean") {
          setMaintenance(res.data.maintenance);
        }
        if (typeof res.data.registration === "boolean") {
          setRegistration(res.data.registration);
        }
        if (typeof res.data.lockMessage === "string" || res.data.lockMessage === null) {
          setLockMessage(res.data.lockMessage);
        }
        setServerStatus(res.data.serverStatus || "ok");
        httpBootstrapOkRef.current = true;
      } catch (e) {
        console.warn(`[${ts()}][StatusContext] get-status probe failed`, e?.message || e);
        httpBootstrapOkRef.current = false;
        setServerStatus("unreachable");
      }
    };

    statusSocket.on("disconnect", (reason) => {
      console.log(`[${ts()}][StatusContext][WS] DISCONNECTED reason=${reason}`);
      if (navigator.onLine) {
        setServerStatus("unreachable");
        probeServerStatus();
      }
    });

    // English comment: WS broadcast from backend
    statusSocket.on(
      "status:update",
      ({ maintenance: newState, lockMessage: newMsg, registration: newReg }) => {
        console.log(
          `[${ts()}][StatusContext][WS] status:update → maintenance=${newState}, registration=${newReg}, lockMessage="${newMsg}"`
        );

        setMaintenance((prev) => newState);
        setLockMessage(newMsg ?? null);
        if (typeof newReg === "boolean") setRegistration(newReg);
      }
    );

    // Heartbeat
    const interval = setInterval(() => {
      statusSocket.emit("hb:ping", { ts: Date.now() });
    }, 4000);

    statusSocket.on("hb:pong", () => {
      if (navigator.onLine) setServerStatus("ok");
    });

    return () => {
      statusSocket.off("connect");
      statusSocket.off("disconnect");
      statusSocket.off("status:update");
      statusSocket.off("hb:pong");
      clearInterval(interval);
    };
  }, []);

  return (
    <StatusContext.Provider
      value={{
        networkStatus,
        serverStatus,
        maintenance,
        lockMessage,
        registration, // English comment: NEW exported field
      }}
    >
      {children}
    </StatusContext.Provider>
  );
}
